<template>
  <div class="ms-richtext-viewer" :class="{ 'ms-validate': isValidate }">
    <label v-if="label" class="label">{{ label }}</label>
    
    <div class="richtext-container" :class="[{'error': errorMessage}, hasBorder ? 'border' : '']">
      <ms-safe-html
        :content="content"
        :allowed-tags="allowedTags"
        :allowed-attrs="allowedAttrs"
        :empty-text="emptyText"
        :class-name="className"
        :use-click-handler="useClickHandler"
        @content-click="onContentClick"
        @link-click="onLinkClick"
      />
    </div>
    
    <div v-if="errorMessage" class="error-text">
      {{ errorMessage }}
    </div>
  </div>
</template>

<script>
import { defineComponent, computed } from 'vue';
import msBaseComponent from '@/components/base/MsBaseComponent.vue';
import MsSafeHtml from '@/components/base/MsSafeHtml.vue';
import { useValidateControl } from '@/composables/validateControl.js';

export default defineComponent({
  name: 'MsRichTextViewer',
  components: {
    MsSafeHtml
  },
  extends: msBaseComponent,
  props: {
    /**
     * Nội dung HTML cần hiển thị
     */
    content: {
      type: String,
      default: ''
    },
    /**
     * Có hiển thị viền xung quanh không
     */
    hasBorder: {
      type: Boolean,
      default: true
    },
    /**
     * Các tag HTML được phép hiển thị
     */
    allowedTags: {
      type: Array,
      default: () => [
        'a', 'b', 'br', 'div', 'em', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
        'i', 'li', 'ol', 'p', 'span', 'strong', 'u', 'ul',
        'table', 'thead', 'tbody', 'tr', 'th', 'td',
        'img', 'hr', 'code', 'pre', 'blockquote'
      ]
    },
    /**
     * Các thuộc tính HTML được phép
     */
    allowedAttrs: {
      type: Array,
      default: () => [
        'href', 'target', 'rel', 'src', 'alt', 'width', 'height', 'class', 'id'
      ]
    },
    /**
     * Văn bản hiển thị khi không có nội dung
     */
    emptyText: {
      type: String,
      default: ''
    },
    /**
     * Lớp CSS bổ sung
     */
    className: {
      type: String,
      default: ''
    },
    /**
     * Có bắt sự kiện click vào nội dung không
     */
    useClickHandler: {
      type: Boolean,
      default: false
    }
  },
  emits: ['contentClick', 'linkClick'],
  setup(props, { emit }) {
    const { errorMessage, validate, isValidate, clearValidate } = useValidateControl({ props });

    // Xử lý sự kiện click vào nội dung
    const onContentClick = (event) => {
      emit('contentClick', event);
    };

    // Xử lý sự kiện click vào link
    const onLinkClick = (linkInfo) => {
      emit('linkClick', linkInfo);
    };

    return {
      errorMessage,
      validate,
      isValidate,
      clearValidate,
      onContentClick,
      onLinkClick
    };
  }
});
</script>

<style lang="scss" scoped>
.ms-richtext-viewer {
  width: 100%;
  
  .label {
    display: block;
    margin-bottom: 4px;
    font-weight: 500;
  }
  
  .richtext-container {
    min-height: 50px;
    max-height: 300px;
    overflow-y: auto;
    background-color: var(--form-white);
    padding: 8px;
    
    &.border {
      border: 1px solid var(--border-color);
      border-radius: var(--border-radius);
    }
    
    &.error {
      border-color: var(--color-danger);
    }
  }
  
  .error-text {
    color: var(--color-danger);
    font-size: 12px;
    margin-top: 4px;
  }
}
</style>
